/**
 * Fraction arithmetic and problem generators for the fraction worksheet.
 *
 * Fractions are pairs of integers, reduced with the Euclidean algorithm. Every generator
 * builds proper fractions first (numerator below denominator) and then checks the
 * constraints a teacher can set — denominators, answers over 1, answers that must or
 * must not need simplifying — so the settings are guarantees, not hopes.
 */

import { type Candidate, collect } from "@/lib/tool-kit/worksheets/arithmetic";
import { pick, type Rng, randInt } from "@/lib/tool-kit/worksheets/random";

export interface Fraction {
  n: number;
  d: number;
}

export type FractionKind =
  | "add"
  | "subtract"
  | "multiply"
  | "divide"
  | "simplify"
  | "equivalent"
  | "compare";
export type FractionSheetType = FractionKind | "mixed";
export type DenominatorMode = "same" | "related" | "different";
export type SimplifyMode = "any" | "required" | "none";
export type Comparison = "<" | ">" | "=";

export interface FractionProblem {
  kind: FractionKind;
  a: Fraction;
  /** The second fraction (operations and comparisons). */
  b: Fraction | null;
  /** The reduced result; for `equivalent`, the fraction with the blank filled in. */
  answer: Fraction;
  comparison: Comparison | null;
}

export interface FractionOptions {
  count: number;
  type: FractionSheetType;
  denominators: DenominatorMode;
  maxDenominator: number;
  /** Whether answers must need simplifying, must already be in lowest terms, or either. */
  answers: SimplifyMode;
  /** Allow improper fractions and answers of 1 or more. */
  improper: boolean;
}

export const MIN_MAX_DENOMINATOR = 4;
const OPERATION_KINDS = ["add", "subtract", "multiply", "divide"] as const;
const MAX_MULTIPLIER = 6;

/* ------------------------------------------------------------------ arithmetic */

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

export function lcm(a: number, b: number): number {
  return (a / gcd(a, b)) * b;
}

export function reduce(f: Fraction): Fraction {
  const divisor = gcd(f.n, f.d) || 1;
  return { n: f.n / divisor, d: f.d / divisor };
}

/** The unreduced result of `a op b`, over the least common denominator for + and −. */
export function unreduced(
  kind: "add" | "subtract" | "multiply" | "divide",
  a: Fraction,
  b: Fraction
): Fraction {
  if (kind === "multiply") {
    return { n: a.n * b.n, d: a.d * b.d };
  }
  if (kind === "divide") {
    return { n: a.n * b.d, d: a.d * b.n };
  }
  const common = lcm(a.d, b.d);
  const left = a.n * (common / a.d);
  const right = b.n * (common / b.d);
  return { n: kind === "add" ? left + right : left - right, d: common };
}

export function compareFractions(a: Fraction, b: Fraction): Comparison {
  const left = a.n * b.d;
  const right = b.n * a.d;
  if (left === right) {
    return "=";
  }
  return left < right ? "<" : ">";
}

/** `3/4`, or `2` for a whole number. */
export function formatFraction(f: Fraction): string {
  return f.d === 1 ? String(f.n) : `${f.n}/${f.d}`;
}

/** `1 3/4` for an improper fraction, otherwise the fraction itself. */
export function formatMixed(f: Fraction): string {
  const whole = Math.trunc(f.n / f.d);
  const rest = f.n % f.d;
  if (rest === 0) {
    return String(whole);
  }
  return whole === 0 ? `${rest}/${f.d}` : `${whole} ${rest}/${f.d}`;
}

/** An answer as written in a key: `7/4 = 1 3/4` when it is improper. */
export function formatAnswerKey(f: Fraction): string {
  return f.d > 1 && f.n > f.d
    ? `${f.n}/${f.d} = ${formatMixed(f)}`
    : formatFraction(f);
}

/** The whole equation for the answer key. */
export function equationText(p: FractionProblem): string {
  const a = formatFraction(p.a);
  const b = p.b ? formatFraction(p.b) : "";
  switch (p.kind) {
    case "add":
      return `${a} + ${b} = ${formatAnswerKey(p.answer)}`;
    case "subtract":
      return `${a} − ${b} = ${formatAnswerKey(p.answer)}`;
    case "multiply":
      return `${a} × ${b} = ${formatAnswerKey(p.answer)}`;
    case "divide":
      return `${a} ÷ ${b} = ${formatAnswerKey(p.answer)}`;
    case "compare":
      return `${a} ${p.comparison ?? "="} ${b}`;
    case "equivalent":
      return `${a} = ${formatFraction(p.answer)}`;
    default:
      return `${a} = ${formatAnswerKey(p.answer)}`;
  }
}

/* ------------------------------------------------------------------- generators */

function properFraction(rng: Rng, d: number): Fraction {
  return { n: randInt(rng, 1, d - 1), d };
}

function denominatorPair(
  rng: Rng,
  mode: DenominatorMode,
  max: number
): [number, number] {
  if (mode === "same") {
    const d = randInt(rng, 2, max);
    return [d, d];
  }
  if (mode === "related") {
    const small = randInt(rng, 2, Math.floor(max / 2));
    const big = small * randInt(rng, 2, Math.floor(max / small));
    return rng() < 0.5 ? [small, big] : [big, small];
  }
  const first = randInt(rng, 2, max);
  let second = randInt(rng, 2, max - 1);
  if (second >= first) {
    second += 1;
  }
  return [first, second];
}

function operationCandidate(
  kind: (typeof OPERATION_KINDS)[number],
  options: FractionOptions,
  rng: Rng
): Candidate<FractionProblem> {
  const max = options.maxDenominator;
  const [d1, d2] =
    kind === "add" || kind === "subtract"
      ? denominatorPair(rng, options.denominators, max)
      : [randInt(rng, 2, max), randInt(rng, 2, max)];
  let a = properFraction(rng, d1);
  let b = properFraction(rng, d2);
  if (kind === "subtract" && compareFractions(a, b) === "<") {
    [a, b] = [b, a];
  }
  const raw = unreduced(kind, a, b);
  const answer = reduce(raw);
  const positive = raw.n > 0;
  const small = options.improper || answer.n < answer.d;
  const reducible = gcd(raw.n, raw.d) > 1;
  const simplifyOk =
    options.answers === "any" ||
    (options.answers === "required" ? reducible : !reducible);
  return {
    item: { kind, a, b, answer, comparison: null },
    ok: positive && small && simplifyOk,
  };
}

function simplifyCandidate(
  options: FractionOptions,
  rng: Rng
): Candidate<FractionProblem> {
  const q = randInt(
    rng,
    2,
    Math.max(2, Math.floor(options.maxDenominator / 2))
  );
  const p = randInt(rng, 1, options.improper ? 2 * q - 1 : q - 1);
  const k = randInt(
    rng,
    2,
    Math.max(2, Math.floor(options.maxDenominator / q))
  );
  const a = { n: p * k, d: q * k };
  const answer = reduce(a);
  return {
    item: { kind: "simplify", a, b: null, answer, comparison: null },
    ok: gcd(p, q) === 1 && a.d <= options.maxDenominator,
  };
}

function equivalentCandidate(
  options: FractionOptions,
  rng: Rng
): Candidate<FractionProblem> {
  const d = randInt(
    rng,
    2,
    Math.max(2, Math.floor(options.maxDenominator / 2))
  );
  const a = reduce(properFraction(rng, d));
  const k = randInt(rng, 2, MAX_MULTIPLIER);
  const answer = { n: a.n * k, d: a.d * k };
  return {
    item: { kind: "equivalent", a, b: null, answer, comparison: null },
    ok: a.d * k <= Math.max(options.maxDenominator, 12),
  };
}

function compareCandidate(
  options: FractionOptions,
  rng: Rng
): Candidate<FractionProblem> {
  const max = options.maxDenominator;
  const [d1, d2] = denominatorPair(rng, options.denominators, max);
  const top = options.improper ? 2 : 1;
  const a: Fraction = { n: randInt(rng, 1, top * d1 - 1), d: d1 };
  // Equal pairs are rare in random draws, so some problems are built equal on purpose.
  if (options.denominators !== "same" && rng() < 0.15) {
    const base = reduce(a);
    const factor = randInt(rng, 2, 3);
    const twin = { n: base.n * factor, d: base.d * factor };
    return {
      item: { kind: "compare", a, b: twin, answer: base, comparison: "=" },
      ok: twin.d <= max * 2 && twin.d !== a.d,
    };
  }
  const b: Fraction = { n: randInt(rng, 1, top * d2 - 1), d: d2 };
  const comparison = compareFractions(a, b);
  return {
    item: { kind: "compare", a, b, answer: reduce(a), comparison },
    ok: comparison !== "=",
  };
}

function keyOf(p: FractionProblem): string {
  return `${p.kind}:${formatFraction(p.a)}:${p.b ? formatFraction(p.b) : ""}`;
}

export interface GeneratedFractions {
  problems: FractionProblem[];
  unmet: number;
}

export function generateFractions(
  raw: FractionOptions,
  rng: Rng
): GeneratedFractions {
  const options = {
    ...raw,
    maxDenominator: Math.max(
      MIN_MAX_DENOMINATOR,
      Math.round(raw.maxDenominator)
    ),
  };
  const make = (r: Rng): Candidate<FractionProblem> => {
    switch (options.type) {
      case "simplify":
        return simplifyCandidate(options, r);
      case "equivalent":
        return equivalentCandidate(options, r);
      case "compare":
        return compareCandidate(options, r);
      case "mixed":
        return operationCandidate(pick(r, OPERATION_KINDS), options, r);
      default:
        return operationCandidate(options.type, options, r);
    }
  };
  const { items, unmet } = collect(options.count, rng, make, keyOf);
  return { problems: items, unmet };
}
